// The estimate editor, as a modal over the engagement card.
//
// It was a set of inline blocks inside EstimateSummaryDisplayCard, which meant editing an option
// happened in a strip a few hundred pixels tall, below the fold, competing for space with tasks,
// the SAM narration and the rest of the workflow card. Pricing a roof is the job, not a footnote
// to it — so it gets the screen.
//
// AUTOSAVE. Every field commits on blur; there is no "unsaved changes" state to lose or warn
// about. The footer still carries an explicit Done, because a modal needs a way out that reads as
// finished rather than abandoned, and "+ Save as new version instead" forks rather than mutating —
// the one action that genuinely branches and so cannot be implicit.
//
// NO BASIS PICKER HERE. How an option selects and prices its materials is chosen when the option
// is CREATED (see the "+ Add option" panel in WorkflowCard, which owns MaterialBasisPicker), so
// the editor no longer re-prices an existing one. Changing the basis now means creating another
// option against it, which is what an estimate SET is for — the alternatives sit side by side
// instead of one of them being overwritten in place.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProposalSurfaceInteractPayload } from '@/components/SAM/samActionTypes';
import { Button } from '@/components/UI/Button';
import { Modal } from '@/components/UI/Modal';
import AddLinePicker from '@/components/Estimates/AddLinePicker';
import ItemLineRow from '@/components/Estimates/ItemLineRow';
import {
  type BasisConformance, CONFORMANCE_DOT, conformanceTally, lineBasisConformance,
} from '@/components/Estimates/basisConformance';
import {
  type EstimateVersionDetail,
  useCompleteEstimateVersionMutation,
  useGetMaterialItemDetailsQuery,
  useGetSuppliersForVersionQuery,
  useOverrideBoqLineMutation,
  useUpdateCommercialTermsMutation,
} from '@/redux/internal-services/estimatesApi';
import { apiErrorMessage, getErrorDetail } from '@/utils/apiError';

const money = (n?: number | null, currency = 'USD') =>
  n === null || n === undefined
    ? '—'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

interface Props {
  detail: EstimateVersionDetail;
  versionCode: string;
  onClose: () => void;
  readOnly?: boolean;
  /** SAM's proposal_surface_interact tool, surface='view_estimate' — expand/collapse a section,
   *  open an item's detail row, or scroll. select_option/close are handled by the parent (they
   *  don't need anything inside this modal), so only those four actions land here. */
  interactSignal?: ProposalSurfaceInteractPayload & { tick: number } | null;
  /** Reports the Materials/Work & Other Services accordion state on every change, whoever caused
   *  it — a SAM expand_section/collapse_section call OR the user's own click on the section
   *  header. Lets the realtime session's screen-awareness push (emitProposalSurfaceContext) stay
   *  accurate even when the user drives this UI by hand instead of through SAM. */
  /** ALL data currently visible in this modal — everything the user sees, not just section
   *  open/closed flags: the commercial totals AND every material/service line's name and amount.
   *  Reported on any change, whoever caused it, so a follow-up question can be answered from
   *  what's actually on screen without SAM needing to re-fetch it. No filtering/restriction. */
  onSectionsChange?: (state: {
    materials_expanded: boolean; services_expanded: boolean;
    totals: { currency: string; material_cost?: number | null; work_cost?: number | null; direct_cost?: number | null; discount_amount?: number | null; grand_total?: number | null };
    materials: Array<{ name: string; amount: number | null }>;
    services: Array<{ name: string; amount: number | null }>;
  }) => void;
}

export default function EstimateEditorModal({ detail, versionCode, onClose, readOnly, interactSignal, onSectionsChange }: Props) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [overrideBoqLine] = useOverrideBoqLineMutation();
  const [updateCommercialTerms] = useUpdateCommercialTermsMutation();
  const [completeEstimateVersion, { isLoading: completing }] = useCompleteEstimateVersionMutation();
  // A material line stores only `product_code`; the manufacturer and human description come from
  // this lookup. Without it every row reads as its raw class code — "GC-01-PRM-M1" rather than
  // "Shingles · GAF" — which is the platform's internal vocabulary, not a name a Sales Agent or
  // their client would recognise.
  const { data: itemDetails, isLoading: loadingItems } =
    useGetMaterialItemDetailsQuery(versionCode, { skip: !versionCode });

  const summary = detail.commercial_summary;
  const currency = summary?.currency ?? 'USD';
  const lines = detail.version.estimate_lines ?? [];
  // GROUPED BY WHAT THE LINE IS, NOT BY WHERE IT CAME FROM.
  //
  // `line_type` reads MATERIAL for every item-resolved line and SERVICE only for BOQ assembly
  // lines — it describes the structure a line was generated from, not its nature. Grouping on it
  // put the supervisor's hourly rate, the truck, the dumpster and the permit fee under a
  // "MATERIALS" heading, which is simply not what they are. The real signal is the item's own
  // `item_type` (M- material, L- labour, E- equipment, S- subcontract, C- consumable), which the
  // item-details lookup now carries.
  const itemLines = useMemo(() => lines.filter((l) => l.line_type === 'MATERIAL'), [lines]);
  const boqLines = useMemo(() => lines.filter((l) => l.line_type === 'SERVICE'), [lines]);
  // `cost_type` is set on the line by the engine (M- material, L- labour, …), so grouping no
  // longer depends on a catalogue lookup that an unresolved line cannot answer. A MATERIAL line
  // that resolved to nothing still belongs under MATERIALS rather than being exiled to work.
  const isMaterial = (l: (typeof lines)[number]) => (
    l.cost_type ? l.cost_type === 'M-' : l.line_type === 'MATERIAL'
  );
  const materials = useMemo(
    () => itemLines.filter(isMaterial),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemLines, itemDetails],
  );
  // Labour, equipment, consumables and subcontract — item lines that are work, not stock. They
  // still render with the material row (they carry a `material` block), just under the heading
  // that describes them.
  const workItems = useMemo(
    () => itemLines.filter((l) => !isMaterial(l)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemLines, itemDetails],
  );
  const services = boqLines;

  useEffect(() => {
    const materialName = (l: (typeof lines)[number]) => {
      if (l.description) return l.description;
      const d = itemDetails?.[l.material?.product_variant_code ?? ''];
      return d?.product_name || d?.variant_name || l.material?.product_code || 'Unnamed material';
    };
    const serviceName = (l: (typeof lines)[number]) => l.description || l.service?.service_code || 'Unnamed service';
    onSectionsChange?.({
      materials_expanded: materialsOpen,
      services_expanded: servicesOpen,
      totals: {
        currency,
        material_cost: summary?.material_cost_total,
        work_cost: summary?.boq_cost_total,
        direct_cost: summary?.direct_cost_total,
        discount_amount: summary?.discount?.amount,
        grand_total: summary?.grand_total,
      },
      materials: [...materials, ...workItems].map((l) => ({ name: materialName(l), amount: l.material?.extended_cost ?? null })),
      services: services.map((l) => ({ name: serviceName(l), amount: l.service?.extended_cost ?? null })),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialsOpen, servicesOpen, materials, workItems, services, summary, itemDetails, currency]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [error, setError] = useState('');
  const [savingField, setSavingField] = useState(false);

  const [discountMode, setDiscountMode] = useState<'percent' | 'fixed'>(
    summary?.discount?.method === 'FIXED_AMOUNT' ? 'fixed' : 'percent',
  );
  const [discountValue, setDiscountValue] = useState(
    String(summary?.discount?.method === 'FIXED_AMOUNT'
      ? summary?.discount?.amount ?? 0
      : summary?.discount?.percent ?? 0),
  );
  // Margin is stored on the estimate like the discount (a MARGIN pricing adjustment, percent or
  // fixed), so it is edited and saved the same way.
  const [marginMode, setMarginMode] = useState<'percent' | 'fixed'>(
    summary?.margin?.method === 'FIXED_AMOUNT' ? 'fixed' : 'percent',
  );
  const [marginValue, setMarginValue] = useState(
    String(summary?.margin?.method === 'FIXED_AMOUNT'
      ? summary?.margin?.amount ?? 0
      : summary?.margin?.percent ?? 0),
  );

  // Only a value the user actually typed is saved. The fields are seeded once at mount, so saving
  // them unconditionally on blur posted a stale discount over one SAM had just written (E13).
  // Tracked per step: editing the discount must never send the margin (or the reverse).
  const commercialDirty = useRef(new Set<'discount' | 'margin'>());
  // Keep the untouched fields in step with the server: SAM (or a re-price) can change the terms
  // while this modal is open.
  useEffect(() => {
    if (!commercialDirty.current.has('discount')) {
      setDiscountMode(summary?.discount?.method === 'FIXED_AMOUNT' ? 'fixed' : 'percent');
      setDiscountValue(String(summary?.discount?.method === 'FIXED_AMOUNT' ? summary?.discount?.amount ?? 0 : summary?.discount?.percent ?? 0));
    }
    if (!commercialDirty.current.has('margin')) {
      setMarginMode(summary?.margin?.method === 'FIXED_AMOUNT' ? 'fixed' : 'percent');
      setMarginValue(String(summary?.margin?.method === 'FIXED_AMOUNT' ? summary?.margin?.amount ?? 0 : summary?.margin?.percent ?? 0));
    }
  }, [summary?.discount?.method, summary?.discount?.amount, summary?.discount?.percent, summary?.margin?.method, summary?.margin?.amount, summary?.margin?.percent]);

  // SAM's proposal_surface_interact tool — drives the same accordion/scroll state a click would.
  useEffect(() => {
    if (!interactSignal) return;
    const { action, section, item_id: itemId } = interactSignal;
    const el = scrollRef.current;
    if (action === 'expand_section' || action === 'collapse_section') {
      const next = action === 'expand_section';
      if (section === 'materials') setMaterialsOpen(next);
      else if (section === 'services') setServicesOpen(next);
    } else if (action === 'open_item' && itemId) {
      setExpanded((p) => ({ ...p, [itemId]: true }));
      // The row it belongs to may still be collapsed — open its section too, or the row (with
      // display: none from its parent's `open && ...` guard) has nothing to scroll into view.
      const inMaterials = materials.some((l) => l.estimate_line_id === itemId);
      const inServices = workItems.some((l) => l.estimate_line_id === itemId) || services.some((l) => l.estimate_line_id === itemId);
      if (inMaterials) setMaterialsOpen(true);
      if (inServices) setServicesOpen(true);
      // Give the section's re-render a tick to mount the row before scrolling to it.
      requestAnimationFrame(() => {
        document.getElementById(itemId)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    } else if (el) {
      if (action === 'scroll_top') el.scrollTo({ top: 0, behavior: 'smooth' });
      else if (action === 'scroll_bottom') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      else if (action === 'scroll_down') el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
      else if (action === 'scroll_up') el.scrollBy({ top: -el.clientHeight * 0.8, behavior: 'smooth' });
      else if (action === 'scroll_to_section' && section) {
        const slug = section.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
        const target = slug.includes('material')
          ? document.getElementById('estimate-section-materials')
          : slug.includes('service') || slug.includes('work')
            ? document.getElementById('estimate-section-services')
            : null;
        target?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactSignal?.tick]);

  // One shared reason for autosaved edits. override_material_line/override_boq_line require one,
  // and prompting per field would make every keystroke a form.
  const AUTOSAVE_REASON = t('estimates.editor.autosaveReason', 'Edited in the estimate editor');

  const commit = async (run: () => Promise<unknown>) => {
    setError('');
    setSavingField(true);
    try {
      await run();
    } catch {
      setError(t('estimates.editor.saveFailed', 'That change could not be saved.'));
    } finally {
      setSavingField(false);
    }
  };

  // ONLY the step(s) the user edited go up. Every other commercial step is deliberately omitted, which the
  // backend reads as "leave it alone": `_recalculate_commercial_summary` carries forward whatever
  // the stored summary holds for a step whose percent AND fixed amount are both absent (see its
  // docstring, and `reprice_with_basis`, which relies on the same behaviour).
  //
  // This used to also send `margin_percent`, seeded once at mount from `summary.margin.percent`,
  // on the belief that omitting it would zero the margin. It did the opposite: a FIXED_AMOUNT
  // margin stores `percent: 0` (see commercialLabel in WorkflowCard.tsx), so editing the discount
  // posted `margin_percent: 0` and converted a fixed margin — $2,000, say — into 0%, quietly
  // taking it out of the quoted total. Seeding once also meant a margin changed by a re-price
  // after this modal opened was reverted by the next discount edit.
  const commitCommercial = () => {
    const dirty = new Set(commercialDirty.current);
    if (!dirty.size) return Promise.resolve();
    return commit(async () => {
      await updateCommercialTerms({
        estimate_version_code: versionCode,
        ...(dirty.has('discount') && (discountMode === 'fixed'
          ? { discount_amount: Number(discountValue) || 0 }
          : { discount_percent: Number(discountValue) || 0 })),
        ...(dirty.has('margin') && (marginMode === 'fixed'
          ? { margin_amount: Number(marginValue) || 0 }
          : { margin_percent: Number(marginValue) || 0 })),
      }).unwrap();
      dirty.forEach((k) => commercialDirty.current.delete(k));
    });
  };

  // Every field already autosaves on blur; Save's own job is to explicitly mark this version
  // COMPLETED (mark_complete is idempotent, so this is a harmless no-op on the common case — an
  // option that already auto-completed at generation). If it can't complete yet (missing
  // measurement/pricing), the backend's validation reason is shown inline and the modal stays
  // open rather than closing on an estimate that isn't actually finished.
  const handleSave = async () => {
    setError('');
    try {
      await completeEstimateVersion(versionCode).unwrap();
      onClose();
    } catch (e) {
      setError(getErrorDetail(e) ?? apiErrorMessage(e, t, t('estimates.editor.completeFailed', 'This estimate could not be completed yet.')));
    }
  };

  // One row per cost type, matching the line grouping above — labour, equipment and subcontract
  // are their own buckets now, so listing only materials and services would leave the rows
  // failing to add up to the direct cost. Each appears only where the estimate carries that cost.
  const totals = [
    { label: t('estimates.editor.materials', 'Materials'), value: summary?.material_cost_total },
    { label: t('estimates.editor.labour', 'Labour'), value: summary?.labour_cost_total || undefined },
    { label: t('estimates.editor.equipment', 'Equipment'), value: summary?.equipment_cost_total || undefined },
    { label: t('estimates.editor.subcontract', 'Subcontracted work'), value: summary?.subcontract_cost_total || undefined },
    { label: t('estimates.editor.permits', 'Permits & fees'), value: summary?.permit_cost_total || undefined },
    { label: t('estimates.editor.workOther', 'Work & other costs'), value: summary?.boq_cost_total },
    { label: t('estimates.editor.directCost', 'Material and Service cost'), value: summary?.direct_cost_total },
  ].filter((row) => row.value !== undefined);

  // The overlay itself must NOT scroll: the modal is height-capped and scrolls internally, and two
  // nested scrollers meant the wheel landed on the outer one, which had nothing to move — so the
  // list looked frozen while its scrollbar thumb slid.
  // The basis this option was CREATED against — a preference the pricing engine falls back from
  // rather than fails on, so every line is checked against it and says whether it held.
  const basis = detail.estimate.generation_method ?? null;
  const basisSupplier = basis?.method_code === 'PREFERRED_SUPPLIER'
    ? basis.parameters?.supplier_code ?? null : null;
  // Only fetched when a supplier basis is actually in force — it is purely to turn "ABCS" into
  // "ABC Supply Co." for the explanation lines, and an option on LOWEST_COST needs no such text.
  const { data: versionSuppliers } = useGetSuppliersForVersionQuery(versionCode, {
    skip: !versionCode || !basisSupplier,
  });
  const basisWanted = basisSupplier
    ? (versionSuppliers ?? []).find((sup) => sup.supplier_code === basisSupplier)?.supplier_name ?? basisSupplier
    : basis?.method_code === 'PREFERRED_MANUFACTURER'
      ? basis.parameters?.manufacturer_code ?? null
      : null;
  const conformanceOf = (l: (typeof lines)[number]) => lineBasisConformance(
    l, basis, itemDetails?.[l.material?.product_variant_code ?? ''],
  );
  const materialsTally = useMemo(
    () => conformanceTally(materials.map(conformanceOf)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [materials, itemDetails, basis],
  );
  const workTally = useMemo(
    () => conformanceTally(workItems.map(conformanceOf)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workItems, itemDetails, basis],
  );

  // One commercial-term row: a %/Fixed switch and a value box, committed on blur.
  const commercialRow = (
    key: 'discount' | 'margin', label: string, mode: 'percent' | 'fixed', setMode: (m: 'percent' | 'fixed') => void,
    value: string, setValue: (v: string) => void, sign: string, valueClass: string,
  ) => (
    <div key={key} className="flex items-center justify-between gap-2 text-xs">
      <span className="text-ui-text2">{label}</span>
      <span className="flex items-center gap-1.5">
        {!readOnly && (
          <span className="flex overflow-hidden rounded-md border border-ui-border">
            {(['percent', 'fixed'] as const).map((m) => (
              <button
                key={m} type="button"
                onClick={() => { commercialDirty.current.add(key); setMode(m); }}
                className={`px-2 py-0.5 text-xs font-medium ${
                  mode === m ? 'bg-accent-fill text-on-accent' : 'bg-ui-card text-ui-text2'
                }`}
              >
                {m === 'percent' ? '%' : t('estimates.editor.fixed', 'Fixed')}
              </button>
            ))}
          </span>
        )}
        <span className="text-ui-text3">{mode === 'fixed' ? `${sign}$` : sign}</span>
        <input
          value={value}
          onChange={(e) => { if (!readOnly) { commercialDirty.current.add(key); setValue(e.target.value); } }}
          onBlur={() => { if (!readOnly) void commitCommercial(); }}
          inputMode="decimal"
          disabled={readOnly}
          className={`w-16 rounded-md border border-ui-border bg-ui-card px-1.5 py-0.5 text-right text-xs ${valueClass} focus:border-ui-ring focus:outline-none disabled:opacity-60`}
        />
      </span>
    </div>
  );

  const renderItemLine = (l: (typeof lines)[number]) => (
    <ItemLineRow
      key={l.estimate_line_id}
      line={l}
      conformance={conformanceOf(l)}
      basisWanted={basisWanted}
      // Keyed by VARIANT, not product: a product has many variants (one per colour) and
      // `getMaterialItemDetails` returns one entry per variant. Looking it up by
      // `product_code` matched nothing, so every row lost its manufacturer, product name,
      // pack size and attributes — and the Manufacturer selector read "Not specified" on
      // lines that plainly have one.
      detail={itemDetails?.[l.material?.product_variant_code ?? '']}
      currency={currency}
      open={!!expanded[l.estimate_line_id]}
      onToggle={() => setExpanded((p) => ({ ...p, [l.estimate_line_id]: !p[l.estimate_line_id] }))}
      commit={commit}
      autosaveReason={AUTOSAVE_REASON}
      readOnly={readOnly}
    />
  );

  return (
    <Modal
      // The option's name, as the title. It used to head the body as an editable "Option name"
      // field — but nothing ever saved it (there is no rename mutation), so typing in it changed
      // the footer button's label and was then discarded on close. Naming the modal after the
      // option is what that field was really being read for: WHICH of the set's options these
      // edits land on.
      title={detail.estimate.estimate_name || t('estimates.editor.title', 'Edit estimate — working version')}
      onClose={onClose}
      maxWidthClassName="max-w-2xl"
      bodyClassName="flex flex-col space-y-0 overflow-hidden p-0"
      closeOnBackdrop={false}
    >
      <p className="flex-shrink-0 px-4 pt-3 text-xs text-ui-text3">
        {t('estimates.editor.workingVersion', 'Working version')}
      </p>
        <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {loadingItems ? (
            <p className="text-xs text-ui-text3">{t('estimates.editor.loadingLines', 'Loading lines…')}</p>
          ) : (
            <>
              <Section
                id="estimate-section-materials"
                title={t('estimates.editor.materialsHeading', 'MATERIALS')}
                count={materials.length}
                tally={materialsTally}
                open={materialsOpen}
                onOpenChange={setMaterialsOpen}
              >
                {materials.map(renderItemLine)}
                {!readOnly && <AddLinePicker kind="material" versionCode={versionCode} currency={currency} commit={commit} />}
              </Section>

              <Section
                id="estimate-section-services"
                title={t('estimates.editor.servicesHeading', 'WORK & OTHER SERVICES')}
                count={workItems.length + services.length}
                tally={workTally}
                open={servicesOpen}
                onOpenChange={setServicesOpen}
              >
                {workItems.map(renderItemLine)}
                {services.map((l) => (
                  <LineRow
                    key={l.estimate_line_id}
                    id={l.estimate_line_id}
                    open={!!expanded[l.estimate_line_id]}
                    onToggle={() => setExpanded((p) => ({ ...p, [l.estimate_line_id]: !p[l.estimate_line_id] }))}
                    title={l.description || l.service?.service_code || l.estimate_line_id}
                    meta={`${money(l.service?.effective_base_rate ?? l.service?.base_rate, currency)} × ${
                      l.service?.effective_required_quantity ?? l.service?.required_quantity ?? 0
                    } ${l.service?.uom ?? ''}`.trim()}
                    sub={l.service?.cost_category}
                    amount={money(l.service?.extended_cost, currency)}
                  >
                    <EditRow
                      readOnly={readOnly}
                      label={t('estimates.editor.rate', 'Rate')}
                      defaultValue={String(l.service?.base_rate ?? '')}
                      onCommit={(v) => commit(async () => {
                        await overrideBoqLine({
                          estimate_boq_line_code: l.estimate_line_id,
                          base_rate: Number(v),
                          override_reason: AUTOSAVE_REASON,
                        }).unwrap();
                      })}
                    />
                    <EditRow
                      readOnly={readOnly}
                      label={t('estimates.editor.quantity', 'Quantity')}
                      defaultValue={String(l.service?.required_quantity ?? '')}
                      onCommit={(v) => commit(async () => {
                        await overrideBoqLine({
                          estimate_boq_line_code: l.estimate_line_id,
                          rated_quantity: Number(v),
                          override_reason: AUTOSAVE_REASON,
                        }).unwrap();
                      })}
                    />
                  </LineRow>
                ))}
                {!readOnly && <AddLinePicker kind="service" versionCode={versionCode} currency={currency} commit={commit} />}
              </Section>
            </>
          )}


          {/* Totals */}
          <div className="space-y-1.5 border-t border-ui-border pt-3">
            {totals.map((row) => (
              <div key={row.label} className="flex items-center justify-between text-xs">
                <span className="text-ui-text2">{row.label}</span>
                <span className="font-medium text-ui-text">{money(row.value, currency)}</span>
              </div>
            ))}

            {commercialRow('margin', t('estimates.editor.margin', 'Margin'), marginMode, setMarginMode, marginValue, setMarginValue, '+', 'text-ui-text')}
            {commercialRow('discount', t('estimates.editor.discount', 'Discount'), discountMode, setDiscountMode, discountValue, setDiscountValue, '-', 'text-danger-text')}

            <div className="flex items-center justify-between border-t border-ui-border pt-2 text-sm">
              <span className="font-semibold text-accent-text">{t('estimates.editor.newTotal', 'New total')}</span>
              <span className="font-semibold text-accent-text">{money(summary?.grand_total, currency)}</span>
            </div>
          </div>

          {error && <p className="text-xs font-medium text-danger-text">{error}</p>}

          {/* Footer */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {/* Field edits are already committed on blur — Save's own job is to mark this
                  version complete (idempotent, so it's a no-op on the common already-completed
                  case) and then close. Cancel just closes without completing; nothing is lost
                  since every field autosaved individually. */}
              {readOnly ? (
                <Button className="flex-1" onClick={onClose}>{t('common.close', 'Close')}</Button>
              ) : (
                <>
                  <Button className="flex-1" onClick={() => void handleSave()} disabled={completing}>
                    {completing ? t('estimates.editor.completing', 'Completing…') : t('common.save', 'Save')}
                  </Button>
                  <Button variant="outline" onClick={onClose}>{t('workflow.cancel', 'Cancel')}</Button>
                </>
              )}
            </div>
            {!readOnly && (
            <p className="text-xs leading-relaxed text-ui-text3">
              {savingField || completing
                ? t('estimates.editor.saving', 'Saving…')
                : t(
                  'estimates.editor.autosaveHint',
                  'Changes save as you make them; Save marks this version complete. This version stays editable until it is sent for approval.',
                )}
            </p>
            )}
          </div>
        </div>
    </Modal>
  );
}

/** Collapsed by default: an option runs to ~30 lines, and opening the editor on a wall of them
 *  buries the totals ladder — the thing a Sales Agent actually came for.
 *  The count on the header says what is inside without making them expand to find out. */
function Section({ id, title, count, tally, children, open: openProp, onOpenChange }: {
  id?: string;
  title: string; count: number;
  /** Basis conformance across the section's lines, shown as dot+count so a substitution is
   *  visible while the section is still collapsed. Omitted where no basis applies. */
  tally?: { matched: number; substituted: number; unavailable: number } | null;
  children: React.ReactNode;
  /** Controlled open state — SAM's proposal_surface_interact (expand_section/collapse_section)
   *  drives these from the parent. Falls back to internal state when omitted. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [localOpen, setLocalOpen] = useState(false);
  const open = openProp ?? localOpen;
  const setOpen = (next: boolean | ((v: boolean) => boolean)) => {
    const resolved = typeof next === 'function' ? next(open) : next;
    if (onOpenChange) onOpenChange(resolved);
    else setLocalOpen(resolved);
  };
  const counts: [BasisConformance, number][] = tally
    ? [['MATCHED', tally.matched], ['SUBSTITUTED', tally.substituted], ['UNAVAILABLE', tally.unavailable]]
    : [];
  return (
    <div id={id}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mb-1.5 flex w-full items-center gap-1.5 text-micro font-semibold uppercase tracking-wide text-accent-text"
        aria-expanded={open}
      >
        <span className={`transition-transform ${open ? 'rotate-90' : ''}`}>&#9654;</span>
        <span>{title}</span>
        <span className="font-normal text-ui-text3">({count})</span>
        <span className="ml-auto flex items-center gap-1.5">
          {counts.filter(([, n]) => n > 0).map(([state, n]) => (
            <span key={state} className="flex items-center gap-1 font-normal normal-case text-ui-text2">
              <span className={`h-2 w-2 rounded-full ${CONFORMANCE_DOT[state]}`} />
              {n}
            </span>
          ))}
        </span>
      </button>
      {open && <div className="space-y-1.5">{children}</div>}
    </div>
  );
}

function LineRow({
  id, open, onToggle, title, meta, sub, badge, amount, children,
}: {
  id?: string;
  open: boolean; onToggle: () => void; title: string; meta: string;
  sub?: string | null; badge?: string | null; amount: string; children: React.ReactNode;
}) {
  return (
    <div id={id} className="rounded-xl border border-ui-border bg-ui-card px-3 py-2">
      <button type="button" onClick={onToggle} className="flex w-full items-start justify-between gap-2 text-left">
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="text-ui-text3">{open ? '▾' : '▸'}</span>
            <span className="truncate text-xs font-semibold text-ui-text">{title}</span>
            {badge && <span className="flex-shrink-0 text-xs font-medium text-ui-text2">{badge}</span>}
            <span className="truncate text-xs text-ui-text3">{meta}</span>
          </span>
          {sub && <span className="mt-0.5 block pl-4 text-micro uppercase tracking-wide text-ui-text3">{sub}</span>}
        </span>
        <span className="flex-shrink-0 text-xs font-semibold text-ui-text">{amount}</span>
      </button>
      {open && <div className="mt-2 space-y-1.5 border-t border-ui-border pt-2">{children}</div>}
    </div>
  );
}

function EditRow({
  label, defaultValue, onCommit, readOnly = false,
}: { label: string; defaultValue: string; onCommit: (v: string) => void | Promise<void>; readOnly?: boolean }) {
  const [value, setValue] = useState(defaultValue);
  return (
    <label className="flex items-center justify-between gap-2 text-xs">
      <span className="text-ui-text3">{label}</span>
      <input
        value={value}
        disabled={readOnly}
        onChange={(e) => setValue(e.target.value)}
        // Commit on blur, not per keystroke: a request per character would race itself and make
        // every partially-typed number a save.
        onBlur={() => { if (value !== defaultValue) void onCommit(value); }}
        inputMode="decimal"
        className="w-24 rounded-md border border-ui-border bg-ui-card px-1.5 py-0.5 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none"
      />
    </label>
  );
}
